import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import ResponsiveIframe from "components/ResponsiveIframe";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useMemo } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  Profession,
  ResponseOfProfession,
  SkuLearningObjective,
} from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import SkuLearningObjectiveForm from "./SkuLearningObjectiveForm";

const useStyles = makeStyles((theme: Theme) =>
  ({
    paperWrapper: {
      width: "100%",
      overflow: "auto",
      backgroundColor: theme.palette.primary.light,
    },
    paper: {
      width: "100%",
      height: "20vh",
      overflow: "auto",
      marginTop: "15px",
    },
    professionLabel: {
      paddingTop: "10px",
      fontWeight: "bold",
      color: theme.palette.primary.dark,
      textDecoration: "underline",
    },
    unspecifiedLabel: {
      paddingTop: "10px",
      color: "grey",
    },
    injectedHtml: {
      "& > *:first-child": {
        marginTop: 0,
      },
      "& > *:last-child": {
        marginBottom: 0,
      },
      padding: "20px",
    },
    descriptionBox: {
      width: "90%",
    },
    actions: {
      width: "10%",
      display: "inline-block",
      verticalAlign: "top",
      textAlign: "right",
    },
    editButton: {
      "&:hover": {
        color: "orange",
      },
    },
    deleteButton: {
      "&:hover": {
        color: "red",
      },
    },
  })
);

type SkuLearningObjectiveBoxProps = {
  skuLearningObjective: SkuLearningObjective;
  deleteLearningObjective?: (id: string) => Promise<void> | undefined;
  showActions?: boolean;
  index: number;
};

const SkuLearningObjectiveBox = ({
  sku,
  skuLearningObjective,
  deleteLearningObjective,
  showActions = true,
  index,
  ...props
}: SkuLearningObjectiveBoxProps & SkuFormProps) => {
  const styles = useStyles(useTheme());
  const [createOpen, createOpenActions] = useBoolean(false);
  const queryClient = useQueryClient();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const { data, isFetching } = useQuery<ResponseOfProfession>(
    ["profession", skuLearningObjective?.professionId!],
    () => skuService.getProfessionById(skuLearningObjective?.professionId!),
    {
      enabled: skuLearningObjective?.professionId !== null,
    }
  );

  const profession = useMemo(() => (data?.data ?? null) as Profession | null, [
    data?.data,
  ]);

  const deleteSkuLearningObjective = async () => {
    try {
      const response = await deleteLearningObjective!(
        skuLearningObjective?.id!
      );
      queryClient.invalidateQueries(["skuLearningObjectives", sku.id!]);
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Paper sx={styles.paperWrapper}>
        <Grid container>
          <Grid item xs={12}>
            <Box display="flex" mt={1} mb={2} ml={2} mr={1}>
              <Box sx={styles.descriptionBox}>
                {!isFetching ? (
                  /*If profession is null, we show the label, otherwise a fragment */
                  profession !== null ? (
                    <Typography sx={styles.professionLabel}>
                      {profession?.name!}
                    </Typography>
                  ) : (
                    <Tooltip
                      title={t({
                        id: "unspecifiedProfessionMessage",
                      })}
                      style={{ textAlign: "left" }}
                    >
                      <Typography sx={styles.unspecifiedLabel}>
                        <T id="unspecified" />
                      </Typography>
                    </Tooltip>
                  )
                ) : (
                  <CircularProgress size={16} />
                )}
                <Paper
                  sx={styles.paper}
                  key={skuLearningObjective.id}
                  data-cy={`objective${index}Text`}
                >
                  <ResponsiveIframe
                    srcDoc={
                      skuLearningObjective.learningObjective?.description!
                    }
                    title="learningObjectiveHtml"
                    containerStyles={{ height: "100%" }}
                  />
                </Paper>
              </Box>
              <Box sx={styles.actions}>
                {showActions && (
                  <>
                    <IconButton
                      color="primary"
                      component="span"
                      sx={styles.editButton}
                      onClick={createOpenActions.setTrue}
                      data-cy={`objective${index}EditButton`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      component="span"
                      sx={styles.deleteButton}
                      onClick={() => deleteOpenActions.setTrue()}
                      data-cy={`objective${index}DeleteButton`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <DeleteEntityDialog
        dialogTitleKey="skuLearningObjectiveDeleteTitle"
        contentKey="skuLearningObjectiveDeleteQuestion"
        open={deleteOpen}
        handleDelete={deleteSkuLearningObjective}
        onCancel={deleteOpenActions.setFalse}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <SkuLearningObjectiveForm
          skuLearningObjective={skuLearningObjective}
          onClose={createOpenActions.setFalse}
          sku={sku}
          submitLabel={t({ id: "skuLearningObjectiveUpdateButton" })}
          {...props}
        />
      </Dialog>
    </>
  );
};

export default SkuLearningObjectiveBox;
